import {
  Box,
  Paper,
  Typography,
  FormControl,
  TextField,
  Button,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { LoginFooterLink } from "components/links/LoginFooterLink";
import { PasswordTextField } from "components/PasswordTextField";
import { ROUTES } from "constants/routes";
import { HttpException } from "exceptions";
import {
  Formik,
  Field,
  Form,
  FormikErrors,
  FormikHelpers,
  FieldProps,
} from "formik";
import useAuth from "hooks/auth";
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";
import React from "react";
import formikStyles from "styles/Formik.module.css";
import styles from "styles/Login.module.css";

export interface RegisterFormState {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  submit?: string;
}

const initialValues: RegisterFormState = {
  email: "",
  first_name: "",
  last_name: "",
  password: "",
};

export default function Register() {
  const { register } = useAuth();
  const router = useRouter();

  const registerHandler = async (
    email: string,
    first_name: string,
    last_name: string,
    password: string,
    setErrors: (errors: FormikErrors<RegisterFormState>) => void,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    try {
      await register({
        first_name,
        last_name,
        email,
        password,
      });

      router.push(ROUTES.LOGIN);
    } catch (err: unknown) {
      const error = err as HttpException;
      setErrors({ submit: error.message });
      setSubmitting(false);
    }
  };
  const formSubmitButtonRef = React.useRef<HTMLButtonElement>(null);

  return (
    <>
      <Head>
        <title>Register</title>
        <meta name="description" content="Register" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <div className={styles.container}>
        <div className={styles.main}>
          <Paper className={styles.box}>
            <div className={styles.logoWrapper}>
              <Image
                src="/NSDS-logo.png"
                alt="Norrizon Logo"
                width={234}
                height={90}
              />
            </div>
            <Typography variant="h2" component="h1" textAlign="center">
              Register
            </Typography>
            <Typography variant="subtitle1" component="p" textAlign="center">
              Create a password to finish registration
            </Typography>
            <Formik
              initialValues={initialValues}
              onSubmit={async (
                { email, password, first_name, last_name },
                { setErrors, setSubmitting }: FormikHelpers<RegisterFormState>
              ) => {
                await registerHandler(
                  email,
                  first_name,
                  last_name,
                  password,
                  setErrors,
                  setSubmitting
                );
              }}
            >
              {({ errors, isSubmitting }) => (
                <Form className={formikStyles.form}>
                  <Box paddingTop={4}>
                    <Field name="email">
                      {({ field }: FieldProps) => (
                        <FormControl fullWidth>
                          <TextField
                            InputProps={{ ...field }}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            id="email"
                            label="Email"
                            variant="outlined"
                            type="email"
                            placeholder="Email address"
                          />
                          <Box paddingTop={1} />
                          <Typography variant="body1">
                            Need to use a different email?&nbsp;&nbsp;
                            <Typography variant="body2" component="span">
                              <a href="mailto:contact-us@email.com">
                                Contact us
                              </a>
                            </Typography>
                          </Typography>
                        </FormControl>
                      )}
                    </Field>
                  </Box>

                  <Box paddingTop={4}>
                    <Field name="first_name">
                      {({ field }: FieldProps) => (
                        <FormControl fullWidth>
                          <TextField
                            InputProps={{ ...field }}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            id="first_name"
                            label="First Name"
                            variant="outlined"
                            placeholder="First Name"
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Box>

                  <Box paddingTop={4}>
                    <Field name="last_name">
                      {({ field }: FieldProps) => (
                        <FormControl fullWidth>
                          <TextField
                            InputProps={{ ...field }}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            id="last_name"
                            label="Last Name"
                            variant="outlined"
                            placeholder="Last Name"
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Box>

                  <Box paddingTop={4}>
                    <PasswordTextField
                      name="password"
                      id="password"
                      label="Password"
                      placeholder="Password"
                    />
                  </Box>
                  <Box paddingTop={4}>
                    <PasswordTextField
                      name="confirmPassword"
                      id="confirmPassword"
                      label="Confirm Password"
                      placeholder="Confirm Password"
                    />
                  </Box>
                  <Box paddingTop={4}>
                    <Field
                      type="checkbox"
                      name="terms"
                      as={FormControlLabel}
                      control={<Checkbox />}
                      label="I have read the Terms and Conditions"
                    />
                  </Box>
                  {errors.submit ? (
                    <Box sx={{ mt: 3 }}>
                      <FormHelperText error>{errors.submit}</FormHelperText>
                    </Box>
                  ) : null}
                  <Box paddingTop={2} />

                  <Button
                    variant="contained"
                    fullWidth
                    component="div"
                    onClick={() => formSubmitButtonRef.current?.click()}
                    disabled={isSubmitting}
                  >
                    <button
                      ref={formSubmitButtonRef}
                      type="submit"
                      disabled={isSubmitting}
                      style={{ display: "none" }}
                    />
                    Create Account
                  </Button>

                  <div className={styles.bottomLogoWrapper}>
                    <Image
                      src="/login-norrizon-logo-new.JPG"
                      alt="Norrizon Logo"
                      width={164}
                      height={82}
                    />
                  </div>
                  <Box paddingTop={4}>
                    <LoginFooterLink
                      route={ROUTES.LOGIN}
                      label="Already have an account? Login"
                    />
                  </Box>
                </Form>
              )}
            </Formik>
          </Paper>
        </div>
      </div>
    </>
  );
}
